import { useCallback, useMemo } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";

import {
  ExpandableNav,
  ExpandableNavItem,
  SectionConfig
} from "@components/ExpandableNav";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { SoftwarePackageType } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import {
  useFieldDeviceConfiguration,
  useFieldDeviceReportedConfig
} from "@libs/api/gateways/field/field-ops-gateway.hooks";
import {
  combinedSoftwarePackages,
  shorthandServiceName
} from "@libs/api/gateways/field/field-ops-gateway.utils";
import {
  useOpsReportsDefinitions,
  useOpsReportsPermissionsQuery
} from "@libs/api/gateways/plt-reports-ops/plt-reports-ops-gateway.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";

import { FieldConfigurationVersion } from "../../../libs/api/gateways/field/field-ops-gateway.dtos";
import { FieldDeviceTabs } from "../field-device-tabs.constants";

interface FieldDeviceSectionsProps {
  id: string;
}

export const FieldDeviceSections: React.FC<FieldDeviceSectionsProps> = ({
  id
}) => {
  const location = useLocation();
  const match = matchPath(
    "/field-mgmt/:fieldDeviceId/:page",
    location.pathname
  );

  const sectionId = match?.params.page;
  const { data: deviceReportedConfig } = useFieldDeviceReportedConfig(id);

  const { data: deviceDesiredConfig } = useFieldDeviceConfiguration(
    id,
    FieldConfigurationVersion.V3_0
  );

  const { data: reportDefinitions } = useOpsReportsDefinitions();

  const navigate = useNavigate();

  const onSectionChange = useCallback(
    (nextId: string) => {
      navigate(`/field-mgmt/${id}/${nextId}`);
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [id]
  );

  const modulesTabs = useMemo(() => {
    // Desired Configuration (based on v3)
    const desiredModules = [
      ...(deviceDesiredConfig?.currentComponents ?? []),
      ...(deviceDesiredConfig?.futureComponents ?? [])
    ]
      .filter(c => c.type === SoftwarePackageType.Module) // Filter for module
      .filter(
        // Filter for uniqueness
        (component, index, self) =>
          index === self.findIndex(c => c.code === component.code)
      );

    const combinedModules = combinedSoftwarePackages(
      desiredModules,
      deviceReportedConfig?.filter(
        item => item.softwarePackageType === SoftwarePackageType.Module
      ) ?? []
    );

    return (
      combinedModules.map(
        module =>
          ({
            id: `modules/${module?.softwarePackageCode}`,
            name: module.name
          } as SectionConfig)
      ) ?? []
    );
  }, [
    deviceDesiredConfig?.currentComponents,
    deviceDesiredConfig?.futureComponents,
    deviceReportedConfig
  ]);

  const serviceTabs = useMemo(() => {
    const desiredServices = [
      ...(deviceDesiredConfig?.currentComponents ?? []),
      ...(deviceDesiredConfig?.futureComponents ?? [])
    ]
      .filter(c => c.type === SoftwarePackageType.Service) // Filter for service
      .filter(
        // Filter for uniqueness
        (component, index, self) =>
          index === self.findIndex(c => c.code === component.code)
      );

    const combinedServices = combinedSoftwarePackages(
      desiredServices,
      deviceReportedConfig?.filter(
        item => item.softwarePackageType === SoftwarePackageType.Service
      ) ?? []
    );

    return (
      combinedServices.map(
        service =>
          ({
            id: `services/${service?.softwarePackageCode}`,
            name: shorthandServiceName(service)
          } as SectionConfig)
      ) ?? []
    );
  }, [
    deviceDesiredConfig?.currentComponents,
    deviceDesiredConfig?.futureComponents,
    deviceReportedConfig
  ]);

  const opsReportsTabs = useMemo(() => {
    return (
      reportDefinitions?.map(report => ({
        id: `ops-reports/${report.name}`,
        name: report.name
      })) ?? []
    );
  }, [reportDefinitions]);

  const { data: hasReadPermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementRead
  );

  const { data: hasReportsPermission } = useOpsReportsPermissionsQuery();

  const items: ExpandableNavItem[] = [
    {
      name: "Device",
      links: Object.values(FieldDeviceTabs.Device),
      hasPermission: hasReadPermission
    },
    {
      name: "Services",
      links: serviceTabs,
      hasPermission: hasReadPermission
    },
    {
      name: "Modules",
      links: modulesTabs,
      hasPermission: hasReadPermission
    },
    {
      name: "Reports",
      links: opsReportsTabs,
      hasPermission: hasReportsPermission
    }
  ];

  return (
    <ExpandableNav
      selectedKey={sectionId}
      onSectionChange={onSectionChange}
      items={items}
    />
  );
};
